import { usePricing } from '@customink/project-service-react-components';
import type {
  Product,
  Project,
} from '@customink/project-service-react-components';
import { useCallback } from 'react';

import { googleAnalyticsEcommEvent } from '@/components/vendors/googleAnalytics';
import type { GA_EVENT_NAME } from '@/constants';
import { useAuth } from '@/contexts/auth/AuthContext';
import { buildEcommObject } from '@/utils/analytics';

type FireEcommEventProps = {
  event: GA_EVENT_NAME;
  voucherCode?: string;
  project?: Project;
  products?: Product[];
};

/**
 * A convenience hook to curry the `googleAnalyticsEcommEvent` function.
 */
export const useEcommEvent = () => {
  const { personalAccount } = useAuth();
  const { data: pricingData } = usePricing();

  // memoizing so it doesn't blow up a dependency array later
  const fireEcommEvent = useCallback(
    ({ event, voucherCode, project, products }: FireEcommEventProps) => {
      const ecommerce = buildEcommObject({
        voucherCode,
        project,
        products,
        groupedQuote: pricingData?.grouped,
        itemizedQuote: pricingData?.itemized,
      });

      googleAnalyticsEcommEvent({
        accountId: personalAccount?.id,
        ecommerce,
        event,
        project,
      });
    },
    [personalAccount?.id, pricingData?.itemized, pricingData?.grouped],
  );

  // destructuring this will prevent us from needing to name the function when importing,
  // that way it will be more consistent across the codebase
  return { fireEcommEvent } as const;
};
